import { useAuth } from '../../contexts/auth';
import { yupResolver } from '@hookform/resolvers/yup';
import { ApiError } from 'api/generated';
import clsx from 'clsx';
import { REACT_APP_BASE_URL } from 'config';
import { useIdentityUsersPasswordResetInitMutation } from 'features/auth/api/repositories/use-identity-users-password-reset-init.mutation';
import { useState } from 'react';
import { SubmitHandler, useForm } from 'react-hook-form';
import { useNavigate } from 'react-router-dom';
import * as yup from 'yup';

type FormValues = {
  code: string;
};

const loginSchema = yup.object().shape({
  code: yup.string().min(3, 'Minimum 3 symbols').max(50, 'Maximum 50 symbols').required('Email is required')
});

const defaultValues: FormValues = {
  code: ''
};

export function ForgotPassword() {
  const { saveAuth } = useAuth();
  const [formStatus, setFormStatus] = useState('');
  const { mutateAsync: useIdentityUsersPasswordResetInit, isLoading: isSubmitting } =
    useIdentityUsersPasswordResetInitMutation();
  const navigate = useNavigate();

  const {
    register,
    handleSubmit,
    formState: { isValid, isLoading: loading, errors, touchedFields }
  } = useForm<FormValues>({
    defaultValues,
    resolver: yupResolver(loginSchema),
    mode: 'onBlur'
  });

  const onSubmit: SubmitHandler<FormValues> = (values: FormValues) => {
    useIdentityUsersPasswordResetInit(
      {
        input: {
          userCode: values.code,
          requestBody: `${REACT_APP_BASE_URL}/auth/reset-password`
        }
      },
      {
        onSuccess: () => {
          navigate('/auth/login', {
            state: {
              successStatus: 'The code has been successfully sent on you email address.'
            }
          });
        },
        onError: (error) => {
          saveAuth(undefined);
          let errorMessage = 'The login detail is incorrect';
          if (error instanceof ApiError) {
            if (error.body.error.message) errorMessage = error.body.error.message;
          }
          setFormStatus(errorMessage);
          console.error(error);
        }
      }
    );
  };

  return (
    <form className="form w-100" onSubmit={handleSubmit(onSubmit)} noValidate id="kt_login_signin_form">
      {/* begin::Heading */}
      <div className="text-center mb-10">
        <h1 className="text-dark mb-3">Reset Password</h1>
        <div className="fs-6 mt-6">
          <p>
            Please enter your login. You will receive a code to reset you password on email that linked to your account.
          </p>
        </div>
      </div>
      {/* begin::Heading */}

      {formStatus && (
        <div className="mb-lg-15 alert alert-danger">
          <div className="alert-text font-weight-bold">{formStatus}</div>
        </div>
      )}

      {/* begin::Form group */}
      <div className="fv-row mb-10">
        <label className="form-label fs-6 fw-bolder text-dark">Login</label>
        <input
          placeholder="Login"
          {...register('code')}
          className={clsx(
            'form-control form-control-lg form-control-solid',
            { 'is-invalid': touchedFields.code && errors.code },
            {
              'is-valid': touchedFields.code && !errors.code
            }
          )}
          type="text"
          name="code"
          autoComplete="off"
        />
        {touchedFields.code && errors.code && (
          <div className="fv-plugins-message-container">
            <span role="alert">{errors.code?.message}</span>
          </div>
        )}
      </div>
      {/* end::Form group */}

      {/* begin::Action */}
      <div className="text-center">
        <button
          type="submit"
          id="kt_sign_in_submit"
          className="btn btn-lg btn-primary w-100 mb-5"
          disabled={isSubmitting || !isValid}
        >
          {!loading && !isSubmitting && <span className="indicator-label">Continue</span>}
          {(loading || isSubmitting) && (
            <span className="indicator-progress" style={{ display: 'block' }}>
              Please wait...
              <span className="spinner-border spinner-border-sm align-middle ms-2"></span>
            </span>
          )}
        </button>
      </div>
      {/* end::Action */}
    </form>
  );
}
